import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import indicativos from "../data/indicativos.json";
import { M_Select, M_InputTextField } from "../M_Components";

const onlyDigits = (s) => String(s || "").replace(/\D+/g, "");

// separa valor gravado em [code, number], suportando "238 123" e "238123"
const splitValue = (val) => {
  const v = String(val || "").trim();
  if (!v) return ["", ""];
  const m = v.match(/^\+?(\d+)\s*(\d*)$/);
  if (m) {
    let c = m[1] || "";
    let n = m[2] || "";
    if (!n && !v.includes(" ") && !v.startsWith("+")) {
      const codes = indicativos
        .map(i => String(i.code || i.value))
        .sort((a, b) => b.length - a.length);
      const found = codes.find(cd => v.startsWith(cd)) || "";
      if (found) {
        c = found;
        n = v.slice(found.length);
      }
    }
    return [c, onlyDigits(n)];
  }
  return ["", ""];
};

export default function PhoneField({
  name,
  map,
  setMap,
  label,
  maxNumbers = 9,
  minNumbers = 4,
  required = false,
  disableNumberUntilPrefix = true,
}) {
  const { t } = useTranslation();

  const [initCode, initNumber] = splitValue(map.get(name));
  const [codeLocal, setCodeLocal] = useState(initCode);
  const [numberLocal, setNumberLocal] = useState((initNumber || "").slice(0, maxNumbers));
  const [error, setError] = useState("");

  useEffect(() => {
    const [c, n] = splitValue(map.get(name));
    setCodeLocal(c);
    setNumberLocal((n || "").slice(0, maxNumbers));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, name]);

  const commit = (c, n) => {
    const v = c ? `${c}${n || ""}` : n || "";
    const newMap = new Map(map);
    newMap.set(name, v);
    setMap(newMap);
  };

  const validate = (c, n) => {
    if (!n) { setError(""); return true; }
    if (n.length < minNumbers) { setError(`Número demasiado curto: mínimo ${minNumbers} dígitos`); return false; }
    if (!c) { setError("Indicativo obrigatório quando o número é preenchido"); return false; }
    setError("");
    return true;
  };

  const onChangeCode = (opt) => {
    const newCode = opt && opt.value ? String(opt.value) : "";
    setCodeLocal(newCode);
    if (!newCode && disableNumberUntilPrefix) {
      setNumberLocal("");
      commit("", "");
      setError("");
    } else {
      commit(newCode, numberLocal);
      validate(newCode, numberLocal);
    }
  };

  const onChangeNumber = (e) => {
    if (disableNumberUntilPrefix && !codeLocal) return;
    const digits = onlyDigits(e.target.value).slice(0, maxNumbers);
    setNumberLocal(digits);
    validate(codeLocal, digits);
  };

  const onBlurNumber = () => {
    commit(codeLocal, numberLocal);
  };

  const optionFromCode = indicativos.find(i => String(i.code || i.value) === String(codeLocal));
  const selectValue = optionFromCode ? { value: String(optionFromCode.code || optionFromCode.value), label: optionFromCode.label } : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <div style={{ minWidth: 150 }}>
          <M_Select
            autocomplete
            label={label || t("Phone.CountryCode")}
            required={required}
            options={indicativos.map(i => ({ value: String(i.code || i.value), label: i.label }))}
            value={selectValue}
            onChangeValue={onChangeCode}
          />
        </div>
        <div style={{ flex: 1 }}>
          <M_InputTextField
            key={codeLocal ? "with-code" : "no-code"}
            placeholder={t("Phone.Number")}
            value={numberLocal}
            onChange={onChangeNumber}
            onBlur={onBlurNumber}
            maxNumbers={maxNumbers}
            required={false}
            disabled={disableNumberUntilPrefix && !codeLocal}
          />
        </div>
      </div>
      {error ? <div style={{ color: "var(--color-red)", fontSize: 12 }}>{error}</div> : null}
    </div>
  );
}
